import React, { useEffect, useState, useCallback, Suspense, lazy } from "react";
import { Switch, Route, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { useTransition } from "react-spring";
import { Box, Alert as MuiAlert, Button } from "@mui/material";

import { selectCurrentUser, selectToken } from "../../redux/user/userSelectors";
import { signInStart } from "../../redux/user/userActions";
import { connectSocket } from "../../redux/socket/socketActions";
import { fetchNotificationsStart } from "../../redux/notification/notificationActions";
import { showAlert } from "../../redux/alert/alertActions";
import { stopImpersonation as stopImpersonationService } from "../../services/superAdminService";

import ProtectedRoute from "../ProtectedRoute/ProtectedRoute";
import Header from "../Header/Header";
import Modal from "../Modal/Modal";
import Alert from "../Alert/Alert";
import Footer from "../Footer/Footer";
import MobileNav from "../MobileNav/MobileNav";
import { DrawerProvider } from "../../context/DrawerContext";

import LoadingPage from "../../pages/LoadingPage/LoadingPage";

const ProfilePage = lazy(() => import("../../pages/ProfilePage/ProfilePage"));
const PostPage = lazy(() => import("../../pages/PostPage/PostPage"));
const ConfirmationPage = lazy(() =>
  import("../../pages/ConfirmationPage/ConfirmationPage")
);
const SettingsPage = lazy(() =>
  import("../../pages/SettingsPage/SettingsPage")
);
const ActivityPage = lazy(() =>
  import("../../pages/ActivityPage/ActivityPage")
);
const NotificationsPage = lazy(() =>
  import("../../pages/NotificationsPage/NotificationsPage")
);
const LoginPage = lazy(() => import("../../pages/LoginPage/LoginPage"));
const SignUpPage = lazy(() => import("../../pages/SignUpPage/SignUpPage"));
const ForgetPwdPage = lazy(() =>
  import("../../pages/ForgetPwdPage/ForgetPwdPage")
);
const HomePage = lazy(() => import("../../pages/HomePage/HomePage"));
const NewPostPage = lazy(() => import("../../pages/NewPostPage/NewPostPage"));
const ExplorePage = lazy(() => import("../../pages/ExplorePage/ExplorePage"));
const NotFoundPage = lazy(() =>
  import("../../pages/NotFoundPage/NotFoundPage")
);
const SuperAdminPage = lazy(() =>
  import("../../pages/SuperAdminPage/SuperAdminPage")
);
const CreatorSubscriptionsPage = lazy(() =>
  import("../../pages/CreatorSubscriptionsPage/CreatorSubscriptionsPage")
);
const SubscriptionsPage = lazy(() =>
  import("../../pages/SubscriptionsPage/SubscriptionsPage")
);

// Wrap SettingsPage with DrawerProvider
const SettingsPageWithDrawer = () => (
  <DrawerProvider>
    <SettingsPage />
  </DrawerProvider>
);

export function UnconnectedApp({
  signInStart,
  modal,
  alert,
  currentUser,
  connectSocket,
  fetchNotificationsStart,
  token,
  showGlobalAlert,
}) {
  const [stopImpersonationBusy, setStopImpersonationBusy] = useState(false);

  const storedToken = token || localStorage.getItem("token");
  const location = useLocation(); // Get current location
  const pathname = location.pathname; // Get current path
  const noHeader =
    location.pathname === "/login" || location.pathname === "/signup";
  const noFooter = location.pathname === "/";
  const isHomePage = pathname === "/";
  const headerHeight = "7.5rem";
  const mobileHeaderHeight = "4rem";

  useEffect(() => {
    if (storedToken) {
      signInStart(null, null, storedToken);
      connectSocket(storedToken);
      fetchNotificationsStart({ authToken: storedToken });
    }
  }, [signInStart, connectSocket, fetchNotificationsStart, storedToken]);

  const handleStopImpersonation = useCallback(async () => {
    if (!storedToken || stopImpersonationBusy) {
      return;
    }
    setStopImpersonationBusy(true);
    try {
      const response = await stopImpersonationService(storedToken);
      showGlobalAlert("Restored your administrator session.");
      signInStart(null, null, response.token);
    } catch (error) {
      showGlobalAlert(
        error?.message || "Unable to restore your administrator session."
      );
    } finally {
      setStopImpersonationBusy(false);
    }
  }, [storedToken, stopImpersonationBusy, showGlobalAlert, signInStart]);

  const renderModals = () => {
    if (modal.modals.length > 0) {
      // Disable scrolling on the body while a modal is active
      document.querySelector("body").setAttribute("style", "overflow: hidden;");
      return modal.modals.map((modal, idx) => (
        <Modal key={idx} component={modal.component} {...modal.props} />
      ));
    } else {
      document.querySelector("body").setAttribute("style", "");
    }
  };

  const transitions = useTransition(alert.showAlert, null, {
    from: {
      transform: "translateY(4rem)",
    },
    enter: {
      transform: "translateY(0rem)",
    },
    leave: {
      transform: "translateY(4rem)",
    },
    config: {
      tension: 500,
      friction: 50,
    },
  });

  const renderApp = () => {
    // Wait for authentication
    if (!currentUser && storedToken) {
      return <LoadingPage />;
    }
    const defaultMarginTop = {
      xs: "28px",
      sm: "64px",
      md: "64px",
      lg: "72px",
    };
    const homePageMarginTop = {
      xs: mobileHeaderHeight,
      sm: headerHeight,
      md: headerHeight,
      lg: headerHeight,
    };
    const marginTopValue = noHeader
      ? 0
      : isHomePage
      ? homePageMarginTop
      : defaultMarginTop;

    return (
      <Box display="flex" flexDirection="column" minHeight="100vh" width="100%" maxWidth="100%">
        {!noHeader && <Header />}
        {renderModals()}
        {currentUser?.isImpersonating && (
          <MuiAlert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleStopImpersonation}
                disabled={stopImpersonationBusy}
              >
                {stopImpersonationBusy ? "Restoring..." : "Return to admin"}
              </Button>
            }
          >
            You are currently impersonating this account.
            {currentUser?.originalAdminUsername &&
              ` Original admin: ${currentUser.originalAdminUsername}.`}
          </MuiAlert>
        )}
        {transitions.map(
          ({ item, props, key }) =>
            item && (
              <Alert key={key} style={props} onClick={alert.onClick}>
                {alert.text}
              </Alert>
            )
        )}
        <Box
          component="main"
          flexGrow={1}
          width="100%"
          maxWidth="100%"
          sx={{
            marginTop: marginTopValue,
            width: "100%",
            maxWidth: "100%",
            padding: isHomePage
              ? 0
              : {
                  xs: 1,
                  sm: 2,
                },
          }}
        >
          <Switch>
            <Route path="/login" component={LoginPage} />
            <Route path="/signup" component={SignUpPage} />
            <Route path="/forgetpwd" component={ForgetPwdPage} />
            <ProtectedRoute exact path="/" component={HomePage} />
            <ProtectedRoute path="/settings" component={SettingsPageWithDrawer} />
            <ProtectedRoute path="/activity" component={ActivityPage} />
            <ProtectedRoute
              path="/notifications"
              component={NotificationsPage}
            />
            <ProtectedRoute path="/new" component={NewPostPage} />
            <ProtectedRoute path="/explore" component={ExplorePage} />
            <ProtectedRoute
              path="/super-admin"
              component={SuperAdminPage}
              requiredRoles={["SuperAdmin"]}
            />
            <ProtectedRoute
              path="/creator/subscriptions"
              component={CreatorSubscriptionsPage}
            />
            <ProtectedRoute
              path="/subscriptions"
              component={SubscriptionsPage}
            />
            <Route exact path="/:username" component={ProfilePage} />
            <Route path="/post/:postId" component={PostPage} />
            <Route path="/auth/verifyEmail" component={ConfirmationPage} />
            <ProtectedRoute
              path="/confirm/:token"
              component={ConfirmationPage}
            />
            <Route component={NotFoundPage} />
          </Switch>
        </Box>
        {/* {!noFooter && <Footer />} */}
        {pathname !== "/login" &&
          pathname !== "/signup" &&
          pathname !== "/new" &&
          currentUser && <MobileNav currentUser={currentUser} />}
      </Box>
    );
  };

  return (
    <div className="app" data-test="component-app" style={{ width: '100%', maxWidth: '100%' }}>
      <Suspense fallback={<LoadingPage />}>{renderApp()}</Suspense>
    </div>
  );
}

const mapStateToProps = (state) => ({
  modal: state.modal,
  alert: state.alert,
  currentUser: selectCurrentUser(state),
  token: selectToken(state),
});

const mapDispatchToProps = (dispatch) => ({
  signInStart: (usernameOrEmail, password, token) =>
    dispatch(signInStart(usernameOrEmail, password, token)),
  connectSocket: (authToken) => dispatch(connectSocket(authToken)),
  fetchNotificationsStart: (options) =>
    dispatch(fetchNotificationsStart(options)),
  showGlobalAlert: (message) => dispatch(showAlert(message)),
});
export default connect(mapStateToProps, mapDispatchToProps)(UnconnectedApp);
